import { BOB_LEVELS, getBobLevel } from './67-bob-levels.mjs';
import { bobWorldForces } from './73-bob-worlds.mjs';

/** Predict descending contact with a floor, raised base or spring in metres.
 * Horizontal extrapolation is advisory: steering/wind can change the landing.
 * Return the earliest surface inside its footprint, never a hidden collision. */
export function bobLanding(state, gravity = bobWorldForces(state).gravity, radius = 0.55) {
  const course = getBobLevel(state.level),
    b = state.bob;
  const surfaces = [
    { x: 0, z: 0, y: bobWorldForces(state).surface, width: Infinity, depth: Infinity },
    ...course.platforms.filter((p) => p.y > 0),
    ...course.trampolines,
  ];
  let best = null;
  for (const p of surfaces) {
    const discriminant = b.vy ** 2 + 2 * gravity * (b.y - radius - p.y);
    if (discriminant < 0) continue;
    const seconds = (b.vy + Math.sqrt(discriminant)) / gravity;
    if (seconds < 0) continue;
    const x = b.x + b.vx * seconds,
      z = b.z + b.vz * seconds;
    if (Math.abs(x - p.x) > p.width / 2 || Math.abs(z - p.z) > p.depth / 2) continue;
    if (!best || seconds < best.seconds) best = { seconds, x, z, y: p.y, id: p.id || 'floor' };
  }
  return best || { seconds: 0, x: b.x, z: b.z, y: 0, id: 'floor' };
}

/** Award the final uninterrupted ballistic arc at the finish post. Height is
 * metres above ground; upward velocity is m/s, gravity m/s². Descending runs
 * earn only their crossing height. Cap at 12m; multipliers affect this bonus
 * alone. Reject non-finite physics instead of persisting an invalid score. */
export function bobFinishBonus(height, upwardSpeed, gravity) {
  if (![height, upwardSpeed, gravity].every(Number.isFinite) || gravity <= 0)
    throw new RangeError('Invalid finish flight.');
  const peak = Math.min(12, Math.max(0, height) + Math.max(0, upwardSpeed) ** 2 / (2 * gravity));
  const centimetres = Math.floor(peak * 100);
  const multiplier = centimetres >= 800 ? 3 : centimetres >= 600 ? 2 : 1;
  const base = centimetres;
  return { height: centimetres / 100, base, multiplier, points: base * multiplier };
}

/** Derive medals from a completed private run. Recovery/assist runs can finish
 * and explore, but cannot set a clean time or mastery medal. No server ranking. */
export function bobResult(s) {
  const course = getBobLevel(s.level);
  const complete = s.status === 'complete';
  const clean = complete && !s.recoveries && !s.assisted && !s.stumbles;
  return {
    complete,
    score: s.score,
    elapsed: s.elapsed,
    cleanTime: clean ? s.elapsed : null,
    medals: [
      complete && 'Finish',
      complete && s.bonusRoutesCompleted.length >= 2 && 'Explorer',
      clean && s.perfects >= 10 && s.elapsed <= course.parTimeMs && 'Mastery',
    ].filter(Boolean),
    breakdown: { ...s.earnings },
    bestChain: s.arcade.bestChain,
    chainReactions: s.arcade.reactions,
    finishBounce: s.finishBounce ? { ...s.finishBounce } : null,
  };
}

/** Merge only validated bounded device records, ignoring unknown or malformed
 * storage fields. A record contains no actor identity, replay or personal data. */
export function mergeBobRecord(previous, result) {
  const safe = (n) => Number.isSafeInteger(n) && n >= 0 && n <= 10000000;
  const medals = ['Finish', 'Explorer', 'Mastery'].filter(
    (m) =>
      (Array.isArray(previous?.medals) && previous.medals.includes(m)) ||
      result?.medals?.includes(m),
  );
  const times = [previous?.cleanTime, result?.cleanTime].filter((n) => safe(n) && n > 0);
  return {
    score: Math.max(
      safe(previous?.score) ? previous.score : 0,
      safe(result?.score) ? result.score : 0,
    ),
    cleanTime: times.length ? Math.min(...times) : null,
    medals,
  };
}

/** Highest selectable world (1..campaign length) from level-keyed private
 * records. Each prior world needs its own Finish medal; score, clean time,
 * assist and recovery do not gate completion. Missing/malformed data stops the
 * contiguous prefix. Ignore inherited/accessor entries without invoking them;
 * this pure projection neither grants a medal nor mutates stored records. */
export function bobUnlockedWorld(records) {
  if (!records || typeof records !== 'object' || Array.isArray(records)) return 1;
  let unlocked = 1;
  while (unlocked < BOB_LEVELS.length) {
    const record = Object.getOwnPropertyDescriptor(records, String(unlocked))?.value;
    const medals =
      record && typeof record === 'object' && !Array.isArray(record)
        ? Object.getOwnPropertyDescriptor(record, 'medals')?.value
        : null;
    if (
      !Array.isArray(medals) ||
      !Object.keys(medals).some(
        (key) =>
          /^(0|[1-9]\d*)$/.test(key) &&
          Number(key) < medals.length &&
          Object.getOwnPropertyDescriptor(medals, key)?.value === 'Finish',
      )
    )
      break;
    unlocked++;
  }
  return unlocked;
}
